import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { EstimateForm } from '../components/estimates/EstimateForm';
import { Button, LoadingSpinner, useToastHelpers } from '../components/ui';
import { useEstimate, useUpdateEstimate } from '../hooks/useEstimates';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';
import type { UpdateEstimateInput } from '@armard/shared';

export const Route = createFileRoute('/estimates_/$id_/edit')({ component: EditEstimatePage });

function EditEstimatePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: estimate, isLoading, error } = useEstimate(id);
  const updateEstimate = useUpdateEstimate();

  const save = async (
    values: import('@armard/shared').CreateEstimateInput | UpdateEstimateInput
  ) => {
    try {
      const updated = await updateEstimate.mutateAsync({ id, ...(values as UpdateEstimateInput) });
      markFormSaved();
      toast.success('Angebot gespeichert');
      await navigate({ to: '/estimates/$id', params: { id: updated.id } });
    } catch (saveError) {
      toast.error(apiErrorMessage(saveError, 'Fehler beim Speichern des Angebots'));
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error || !estimate ? (
          <div className="text-center py-12">
            <p className="text-red-400">Angebot nicht gefunden</p>
            <Button className="mt-4" onClick={() => navigate({ to: '/estimates' })}>
              Zurück zur Liste
            </Button>
          </div>
        ) : estimate.status !== 'draft' ? (
          <div className="max-w-xl mx-auto py-12 text-center space-y-4">
            <h1 className="text-xl text-white">
              Dieses Angebot kann nicht mehr bearbeitet werden.
            </h1>
            <p className="text-white/60">
              Nur Angebote im Entwurf lassen sich ändern. Der aktuelle Status ist „{estimate.status}
              “.
            </p>
            <Button onClick={() => navigate({ to: '/estimates/$id', params: { id } })}>
              Zum Angebot
            </Button>
          </div>
        ) : (
          <EstimateForm
            key={estimate.id}
            initial={estimate}
            title="Angebot bearbeiten"
            submitLabel="Änderungen speichern"
            isSaving={updateEstimate.isPending}
            onCancel={() => navigate({ to: '/estimates/$id', params: { id } })}
            onSave={save}
          />
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
